import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { JobPosition } from '@restworld/utility-types';

import { shouldShowMensilita } from '@restworld/utils-common';

import { getSalary } from './use-salary';
export const useJobPositionDetails = ({ jobPositionApiData }: { jobPositionApiData: Partial<JobPosition> }) => {
  const { t } = useTranslation('common');
  const {
    days_required_per_week_number,
    shift_category_var,
    occupation,
    restaurant,
    contract_type_var,
    benefits,
    season_starts_at,
    season_ends_at,
    salary_month_number,
    id,
    salary_currency,
  } = jobPositionApiData || {};

  const salary = getSalary({ jobPositionApiData, t });
  const seasonalAccommodation = useMemo(() => {
    if (!benefits) return false;
    return !!benefits.find((benefit) => benefit?.benefit_var.key.startsWith('seasonal_accommodation'));
  }, [benefits]);

  const contractTerm = useMemo(() => {
    if (!contract_type_var) return '';

    const contract = contract_type_var.label;
    if (!!season_starts_at && !!season_ends_at) {
      return t('job_position_contract_term_from_to', { contract, start: season_starts_at, end: season_ends_at });
    }
    if (season_starts_at) {
      return t('job_position_contract_term_from', { contract, start: season_starts_at });
    }
    if (season_ends_at) {
      return t('job_position_contract_term_until', { contract, end: season_ends_at });
    }
    return contract;
  }, [contract_type_var, season_starts_at, season_ends_at, t]);

  const shiftsSubTitle = days_required_per_week_number
    ? t('job_position_days_per_week', { days: days_required_per_week_number })
    : t('job_position_days_to_be_defined');

  const shiftsTitle = shift_category_var?.label || t('shifts');

  const shifts = {
    title: shiftsTitle,
    subTitle: shiftsSubTitle,
  };

  const jobDetailBlockApiProps = {
    jobPositionDetails: {
      companyName: restaurant?.restaurant_name,
      companyLogo: restaurant?.restaurant_logo,
      title: occupation?.label_it?.label,
      jobLocation: restaurant?.address,
      jobLocality: restaurant?.locality,
      salary,
      contractTerm,
      shifts,
      seasonalAccommodation,
      // Mensilità label only for monthly pay at an Italian venue (see helper):
      // RAL / hourly / foreign jobs must not show a "per N mensilità" chip.
      salaryMonthNumber:
        shouldShowMensilita(jobPositionApiData) && salary_month_number ? salary_month_number : undefined,
      salaryCurrency: salary_currency,
      id,
    },
    showRestaurant: jobPositionApiData?.show_restaurant_details,
  };

  return jobDetailBlockApiProps;
};
